import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Capacitor } from '@capacitor/core';
import { NOTIFICATION_BLOCKED_EVENT } from '@/lib/notificationBlockedDialog';

type Platform =
  | 'native-ios'
  | 'native-android'
  | 'ios-safari'
  | 'android-chrome'
  | 'android-firefox'
  | 'desktop-chrome'
  | 'desktop-edge'
  | 'desktop-firefox'
  | 'desktop-safari'
  | 'generic';

function detectPlatform(): Platform {
  if (Capacitor.isNativePlatform()) {
    const p = Capacitor.getPlatform();
    if (p === 'ios') return 'native-ios';
    if (p === 'android') return 'native-android';
  }
  if (typeof navigator === 'undefined') return 'generic';
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  const isFirefox = /Firefox\//.test(ua);
  const isEdge = /Edg\//.test(ua);
  const isChrome = /Chrome\//.test(ua) && !isEdge;
  const isSafari = /Safari\//.test(ua) && !isChrome && !isEdge && !isFirefox;

  if (isIOS) return 'ios-safari';
  if (isAndroid) {
    if (isFirefox) return 'android-firefox';
    return 'android-chrome';
  }
  if (isEdge) return 'desktop-edge';
  if (isFirefox) return 'desktop-firefox';
  if (isSafari) return 'desktop-safari';
  if (isChrome) return 'desktop-chrome';
  return 'generic';
}

interface Instructions {
  label: string;
  steps: Array<React.ReactNode>;
}

function getInstructions(platform: Platform): Instructions {
  switch (platform) {
    case 'native-ios':
      return {
        label: 'iOS app',
        steps: [
          <>Open <strong>Settings</strong> on your iPhone or iPad.</>,
          <>Scroll down and tap <strong>LupusLogix</strong>.</>,
          <>Tap <strong>Notifications</strong>.</>,
          <>Turn <strong>Allow Notifications</strong> on.</>,
          <>Return here and tap <strong>I&apos;ve enabled notifications</strong>.</>,
        ],
      };
    case 'native-android':
      return {
        label: 'Android app',
        steps: [
          <>Open <strong>Settings</strong> on your device.</>,
          <>Tap <strong>Apps</strong> &rarr; <strong>LupusLogix</strong>.</>,
          <>Tap <strong>Notifications</strong>.</>,
          <>Turn <strong>Allow notifications</strong> on.</>,
          <>Return here and tap <strong>I&apos;ve enabled notifications</strong>.</>,
        ],
      };
    case 'ios-safari':
      return {
        label: 'Safari on iPhone or iPad',
        steps: [
          <>For reliable notifications, add LupusLogix to your Home Screen: tap the <strong>Share</strong> button, then <strong>Add to Home Screen</strong>.</>,
          <>Open the LupusLogix app from your Home Screen.</>,
          <>If still blocked, open <strong>Settings</strong> &rarr; <strong>Notifications</strong> &rarr; <strong>LupusLogix</strong> and turn <strong>Allow Notifications</strong> on.</>,
          <>Return here and tap <strong>I&apos;ve enabled notifications</strong>.</>,
        ],
      };
    case 'android-chrome':
      return {
        label: 'Chrome on Android',
        steps: [
          <>Tap the <strong>lock</strong> (or <strong>tune</strong>) icon next to the address bar.</>,
          <>Tap <strong>Permissions</strong> (or <strong>Site settings</strong>).</>,
          <>Tap <strong>Notifications</strong> and set it to <strong>Allow</strong>.</>,
          <>Return here and tap <strong>I&apos;ve enabled notifications</strong>.</>,
        ],
      };
    case 'android-firefox':
      return {
        label: 'Firefox on Android',
        steps: [
          <>Tap the <strong>shield</strong> icon next to the address bar.</>,
          <>Tap <strong>Site permissions</strong>.</>,
          <>Set <strong>Notifications</strong> to <strong>Allow</strong>.</>,
          <>Return here and tap <strong>I&apos;ve enabled notifications</strong>.</>,
        ],
      };
    case 'desktop-chrome':
      return {
        label: 'Chrome on desktop',
        steps: [
          <>Click the <strong>lock</strong> (or <strong>tune</strong>) icon at the left of the address bar.</>,
          <>Click <strong>Site settings</strong>.</>,
          <>Find <strong>Notifications</strong> and change it to <strong>Allow</strong>.</>,
          <>Return here and tap <strong>I&apos;ve enabled notifications</strong>.</>,
        ],
      };
    case 'desktop-edge':
      return {
        label: 'Microsoft Edge',
        steps: [
          <>Click the <strong>lock</strong> icon at the left of the address bar.</>,
          <>Click <strong>Permissions for this site</strong>.</>,
          <>Set <strong>Notifications</strong> to <strong>Allow</strong>.</>,
          <>Return here and tap <strong>I&apos;ve enabled notifications</strong>.</>,
        ],
      };
    case 'desktop-firefox':
      return {
        label: 'Firefox on desktop',
        steps: [
          <>Click the <strong>shield</strong> or <strong>lock</strong> icon at the left of the address bar.</>,
          <>Click <strong>Connection secure</strong> &rarr; <strong>More information</strong>, then open <strong>Permissions</strong>.</>,
          <>Find <strong>Send Notifications</strong> and clear or change it to <strong>Allow</strong>.</>,
          <>Return here and tap <strong>I&apos;ve enabled notifications</strong>.</>,
        ],
      };
    case 'desktop-safari':
      return {
        label: 'Safari on macOS',
        steps: [
          <>Open Safari and choose <strong>Safari</strong> &rarr; <strong>Settings</strong> from the menu bar.</>,
          <>Open the <strong>Websites</strong> tab and select <strong>Notifications</strong>.</>,
          <>Find LupusLogix in the list and set it to <strong>Allow</strong>.</>,
          <>Return here and tap <strong>I&apos;ve enabled notifications</strong>.</>,
        ],
      };
    default:
      return {
        label: 'your browser',
        steps: [
          <>Open your browser&apos;s site settings for this page (often the <strong>lock</strong> icon next to the address bar).</>,
          <>Find <strong>Notifications</strong> and change it to <strong>Allow</strong>.</>,
          <>Return here and tap <strong>I&apos;ve enabled notifications</strong>.</>,
        ],
      };
  }
}

/**
 * Global dialog mounted once at the app root. Listens for the
 * NOTIFICATION_BLOCKED_EVENT window event and shows platform-specific
 * instructions for re-enabling notifications.
 */
export const NotificationBlockedDialog = () => {
  const [open, setOpen] = useState(false);
  const platform = useMemo(() => detectPlatform(), []);
  const instructions = useMemo(() => getInstructions(platform), [platform]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(NOTIFICATION_BLOCKED_EVENT, handler);
    return () => window.removeEventListener(NOTIFICATION_BLOCKED_EVENT, handler);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div className="flex-1 space-y-1">
              <DialogTitle>Notifications are blocked</DialogTitle>
              <DialogDescription>
                We can&apos;t ask again — your device has blocked notifications
                for LupusLogix. Follow the steps for {instructions.label} to
                turn them back on.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ol className="mt-2 list-decimal space-y-2 rounded-md bg-muted/50 p-4 pl-6 text-sm text-foreground">
          {instructions.steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>

        <p className="text-xs text-muted-foreground">
          You won&apos;t receive medication or appointment reminders on this
          device until notifications are allowed.
        </p>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Close
          </Button>
          <Button onClick={() => window.location.reload()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            I&apos;ve enabled notifications
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
